import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, X, Calendar, AlertTriangle } from "lucide-react";
import { useSmartReminders, useDismissReminder } from "@/hooks/useSmartReminders";
import { useEmployee } from "@/hooks/useEmployee";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

export default function SmartReminders() {
  const { data: currentEmployee } = useEmployee();
  const { data: reminders, isLoading } = useSmartReminders(currentEmployee?.id);
  const dismissReminder = useDismissReminder();
  const navigate = useNavigate();

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      high: "bg-red-500",
      medium: "bg-yellow-500",
      low: "bg-blue-500",
    };
    return colors[priority] || "bg-gray-500";
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === "high") return <AlertTriangle className="h-4 w-4" />;
    if (priority === "medium") return <Bell className="h-4 w-4" />;
    return <Calendar className="h-4 w-4" />;
  };

  const handleDismiss = (reminderId: string) => {
    dismissReminder.mutate(reminderId);
  };

  const handleAction = (reminder: any) => {
    if (reminder.action_url) {
      navigate(reminder.action_url);
    }
  };

  const upcomingReminders = reminders?.filter(
    (r) => new Date(r.reminder_date) > new Date()
  );
  const overdueReminders = reminders?.filter(
    (r) => new Date(r.reminder_date) <= new Date()
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">التذكيرات الذكية</h1>
          <p className="text-muted-foreground">إدارة جميع التذكيرات والإشعارات المهمة</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-lg py-2 px-4">
            <Bell className="ml-2 h-4 w-4" />
            {reminders?.length || 0} تذكير نشط
          </Badge>
        </div>
      </div>

      {/* التذكيرات المتأخرة */}
      {overdueReminders && overdueReminders.length > 0 && (
        <Card className="border-red-500">
          <CardHeader>
            <CardTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              تذكيرات متأخرة ({overdueReminders.length})
            </CardTitle>
            <CardDescription>تذكيرات تجاوزت موعدها وتحتاج لاتخاذ إجراء</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {overdueReminders.map((reminder) => (
              <Card key={reminder.id} className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(reminder.priority)}
                        <h3 className="font-semibold">{reminder.message}</h3>
                        <Badge className={getPriorityColor(reminder.priority)}>
                          {reminder.priority === "high" ? "عاجل" : reminder.priority === "medium" ? "متوسط" : "عادي"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        نوع: {reminder.reminder_type} | الموعد: {format(new Date(reminder.reminder_date), "PPP", { locale: ar })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {reminder.action_url && (
                        <Button onClick={() => handleAction(reminder)} size="sm">
                          اتخاذ إجراء
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDismiss(reminder.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* التذكيرات القادمة */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            تذكيرات قادمة
          </CardTitle>
          <CardDescription>التذكيرات المجدولة للأيام القادمة</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">جاري التحميل...</div>
          ) : upcomingReminders?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد تذكيرات قادمة
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingReminders?.map((reminder) => (
                <Card key={reminder.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          {getPriorityIcon(reminder.priority)}
                          <h3 className="font-semibold">{reminder.message}</h3>
                          <Badge className={getPriorityColor(reminder.priority)}>
                            {reminder.priority === "high" ? "عاجل" : reminder.priority === "medium" ? "متوسط" : "عادي"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          نوع: {reminder.reminder_type} | الموعد: {format(new Date(reminder.reminder_date), "PPP", { locale: ar })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {reminder.action_url && (
                          <Button onClick={() => handleAction(reminder)} variant="outline" size="sm">
                            عرض التفاصيل
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDismiss(reminder.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}